/**
 * The install hint: what to offer, and to whom.
 *
 * Two worlds. Chromium (Android, desktop) fires `beforeinstallprompt`, which we keep so the customer can be
 * offered a real one-tap install. iOS Safari fires nothing and has no API at all — the only route is the share
 * sheet, so there we can only explain it. Everywhere else (an already-installed window, a browser that cannot
 * install, or someone who has said no once) the hint must simply not exist.
 */

/** Chromium-only event; not in lib.dom. */
export interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export const INSTALL_DISMISSED_KEY = 'sf.pwa.installDismissed';

export type InstallHintMode = 'prompt' | 'ios';

export interface InstallEnv {
  /** A `beforeinstallprompt` event is in hand. */
  hasPrompt: boolean;
  /** Already running as an installed app — there is nothing left to offer. */
  standalone: boolean;
  ios: boolean;
  /** The customer has closed this hint before. Once is a hint; twice is a nag. */
  dismissed: boolean;
}

/** Null means: show nothing. */
export function installHintMode(env: InstallEnv): InstallHintMode | null {
  if (env.standalone || env.dismissed) return null;
  if (env.hasPrompt) return 'prompt';
  return env.ios ? 'ios' : null;
}

/**
 * iPhone, iPod, iPad — including iPadOS 13+, which reports itself as a Mac and can only be told apart by the
 * fact that Macs do not have a touchscreen.
 */
export function isIos(userAgent: string, maxTouchPoints: number): boolean {
  if (/iphone|ipod|ipad/i.test(userAgent)) return true;
  return /macintosh/i.test(userAgent) && maxTouchPoints > 1;
}

export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  const displayMode = window.matchMedia?.('(display-mode: standalone)').matches ?? false;
  // Safari's own, older flag — the only signal iOS gives.
  const iosStandalone = (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
  return displayMode || iosStandalone;
}

/** localStorage can throw (private mode, storage disabled); a hint is never worth an exception. */
export function readDismissed(): boolean {
  try {
    return window.localStorage.getItem(INSTALL_DISMISSED_KEY) === '1';
  } catch {
    return false;
  }
}

export function writeDismissed(): void {
  try {
    window.localStorage.setItem(INSTALL_DISMISSED_KEY, '1');
  } catch {
    /* ignore */
  }
}

// ---------------------------------------------------------------------------------------------
// The deferred prompt store.
//
// `beforeinstallprompt` fires during the first navigation, long before the device page mounts, so it is
// captured at boot (main.tsx) and kept here for whichever component later asks for it.
// ---------------------------------------------------------------------------------------------

let deferred: BeforeInstallPromptEvent | null = null;
let installed = false;
const listeners = new Set<() => void>();

const emit = () => {
  for (const l of listeners) l();
};

/** Called once from main.tsx, before React mounts. */
export function watchInstallability(): void {
  if (typeof window === 'undefined') return;
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault(); // keep the browser's own mini-infobar out of the way; we ask in our own words
    deferred = e as BeforeInstallPromptEvent;
    emit();
  });
  window.addEventListener('appinstalled', () => {
    deferred = null;
    installed = true;
    writeDismissed();
    emit();
  });
}

export function subscribeInstallability(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export const hasDeferredPrompt = (): boolean => deferred !== null;
export const wasInstalled = (): boolean => installed;

/** Shows the browser's install dialog. Resolves once the customer has answered. */
export async function promptInstall(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
  const event = deferred;
  if (!event) return 'unavailable';
  deferred = null; // a deferred prompt may only be used once
  emit();
  await event.prompt();
  const { outcome } = await event.userChoice;
  return outcome;
}
